/* eslint-disable react/no-array-index-key */

import { alpha, useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
// BASE ------------------------------------------------------------------------
import React, { useState, useLayoutEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

// SELECTORS, ACTIONS, UTILS ------------------------------------------------------------------------
import { profileSelectors } from 'app-components/flux/profile';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Avatar from '@mui/material/Avatar';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
// MUI COMPONENTS ------------------------------------------------------------------------
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

// PATH RELATIVE  ------------------------------------------------------------------------
import { avatarSxMain, getAppIcon } from '../aiConfig';

export default function SamplePromptsPage(props) {
  const { samplePrompts, samplePromptsHaveIcons, submitAutoPrompt } = props;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [gridXs, setGridXs] = useState(6); // Initial xs value for Grid items
  const stackRef = useRef(null); // Reference to the Stack for ResizeObserver
  const { classes } = useStyles();
  const profile = useSelector(profileSelectors.getProfile);
  const theme = useTheme();
  const AppIcon = getAppIcon(theme);

  const totalPrompts = samplePrompts.length;
  const canGoBack = currentIndex > 0;
  const canGoForward = currentIndex + 4 < totalPrompts;

  const handleNext = () => {
    if (canGoForward) {
      setCurrentIndex(currentIndex + 4);
    }
  };

  const handlePrev = () => {
    if (canGoBack) {
      setCurrentIndex(currentIndex - 4);
    }
  };

  useLayoutEffect(() => {
    const handleResize = (entries) => {
      if (entries.length > 0) {
        const { width } = entries[0].contentRect;

        // Calculate the xs value based on the width
        if (width < 600) {
          if (gridXs !== 6) {
            requestAnimationFrame(() => {
              setGridXs(6); // 1 item per row
            });
          }
        } else {
          if (gridXs !== 3) {
            requestAnimationFrame(() => {
              setGridXs(3); // 4 items per row
            });
          }
        }
      }
    };

    const observer = new ResizeObserver(handleResize);
    if (stackRef.current) {
      observer.observe(stackRef.current);
    }
    return () => {
      observer.disconnect();
    };
  }, [gridXs]);

  const displayedPrompts = samplePrompts.slice(currentIndex, currentIndex + 4);

  let profileName = null;
  if (profile && /\S/.test(profile.firstName) && profile.firstName) {
    profileName = profile.firstName;
  }

  const sliceSize = samplePromptsHaveIcons ? 2 : 0;

  return (
    <Stack
      ref={stackRef}
      id='sample-prompts'
      alignItems='center'
      justifyContent='center'
      margin={4}
      padding={4}
      gap={3}
      sx={{ minHeight: 420, flexGrow: 1 }}
    >
      <Avatar src={AppIcon} sx={avatarSxMain} />
      <Stack gap={1} alignItems='center' justifyContent='center'>
        <Typography variant='displayBold' className={classes.profileName}>
          {profileName ? `Hello, ${profileName}!` : 'Hello!'}
        </Typography>
      </Stack>

      <Stack className={classes.promptContainer}>
        {canGoBack && (
          <IconButton onClick={handlePrev}>
            <ArrowBackIcon />
          </IconButton>
        )}
        <Grid container spacing={2}>
          {displayedPrompts.map((prompt, index) => (
            <Grid item key={index} xs={gridXs}>
              <Card onClick={() => submitAutoPrompt(prompt, false)} className={classes.prompt}>
                <CardContent sx={{ height: '100%', minHeight: '10px' }}>
                  {samplePromptsHaveIcons && (
                    <Typography
                      variant='headlineMedium'
                      component='span'
                      sx={{
                        padding: 1,
                        borderRadius: 1,
                        background: `linear-gradient(57deg, ${alpha(theme.palette.data.sequenceLight[0], 0.16)} 0%, ${alpha(
                          theme.palette.data.sequenceLight[9],
                          0.16,
                        )} 100%)`,
                      }}
                    >
                      {prompt.slice(0, sliceSize)} {/* Cycle through emojis */}
                    </Typography>
                  )}
                  <Typography variant='paragraphRegular' sx={{ marginTop: 2 }}>
                    {prompt.slice(sliceSize)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        {canGoForward && (
          <IconButton onClick={handleNext}>
            <ArrowForwardIcon />
          </IconButton>
        )}
      </Stack>
    </Stack>
  );
}

const useStyles = makeStyles()((theme) => ({
  profileName: {
    background: `-webkit-linear-gradient(50deg, ${theme.palette.data.sequence[0]} 0%, ${theme.palette.data.sequence[6]} 100%)`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    display: 'inline',
  },
  promptContainer: { display: 'flex', flexDirection: 'row', alignItems: 'center' },
  prompt: {
    cursor: 'pointer',
    height: '100%',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'scale(1.05)',
      boxShadow: 3,
    },
  },
}));

SamplePromptsPage.propTypes = {
  samplePrompts: PropTypes.array.isRequired,
  samplePromptsHaveIcons: PropTypes.bool,
  submitAutoPrompt: PropTypes.func,
};
